import { Subhead } from "../components/SettingsLayout";
import { FileCodeIcon, GitCommitIcon, XIcon } from "@primer/octicons-react";
import { rp } from "../api/client";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useCommit, useLog } from "../api/hooks";
import { DiffView } from "../components/Diff";
import { Avatar, CopyButton, Empty, Query, Sha, TimeAgo } from "../components/common";
import type { LogEntry } from "../api/types";
import { cn } from "../lib/utils";
import { messageBody, parseSig } from "../components/format";
import { EvidenceIcon } from "../components/StateLabel";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { List, ListItem, ListItemMeta, ListItemSeparator, ListItemTitle } from "../components/ui/list";
import { Tooltip } from "../components/ui/tooltip";

const VERB: Record<string, string> = { land: "landed", revert: "reverted", import: "imported", sync: "pulled from remote" };

const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

/** The commit history (of one path, when the URL names one), grouped by day on a timeline. */
export function Commits() {
  const { repo = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const path = params.get("path") ?? undefined;
  const cursor = params.get("cursor") ?? undefined;
  const q = useLog(repo, { path, limit: 50, cursor });
  return (
    <>
      <Subhead
        title="History"
        actions={
          path && (
            <Badge tone="blue">
              <span className="font-mono">{path}</span>
              <Button variant="ghost" size="icon-sm" className="size-4 text-info" onClick={() => setParams({})} aria-label="Show all history">
                <XIcon size={12} />
              </Button>
            </Badge>
          )
        }
      />
      <Query q={q}>
        {(d) => {
          if (d.entries.length === 0)
            return (
              <Card>
                <Empty icon={GitCommitIcon}>No commits{path ? " touch this path" : ""}.</Empty>
              </Card>
            );
          const days: { label: string; entries: typeof d.entries }[] = [];
          for (const e of d.entries) {
            const label = day(e.ts);
            if (days.at(-1)?.label === label) days.at(-1)!.entries.push(e);
            else days.push({ label, entries: [e] });
          }
          const mark = "flex h-10 items-center gap-3 text-muted-foreground [&>svg]:shrink-0";
          return (
            <div className="mt-4">
              {days.map((g) => (
                <section key={g.label}>
                  <h3 className={cn(mark, "text-sm font-normal")}>
                    <GitCommitIcon size={16} className="ml-2" /> Commits on {g.label}
                  </h3>
                  {/* The timeline: a line down from each day's mark, beside that day's commits. */}
                  <div className="ml-[15px] border-l-2 border-border-muted py-1 pl-6">
                    <Card className="mt-0">
                      <List>
                        {g.entries.map((e) => (
                          <CommitRow key={e.seq} repo={repo} e={e} />
                        ))}
                      </List>
                    </Card>
                  </div>
                </section>
              ))}
              {d.cursor ? (
                <div className="mt-4 text-center">
                  <Button onClick={() => setParams({ ...(path ? { path } : {}), cursor: d.cursor! })}>Load older commits</Button>
                </div>
              ) : (
                <p className={cn(mark, "text-sm")}>
                  <GitCommitIcon size={16} className="ml-2" /> End of commit history{path ? " for this path" : ""}
                </p>
              )}
            </div>
          );
        }}
      </Query>
    </>
  );
}

function CommitRow({ repo, e }: { repo: string; e: LogEntry }) {
  const who = e.agent ?? e.actor;
  return (
    <ListItem>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <ListItemTitle asChild className="truncate text-base">
            {e.change_id ? (
              <Link to={`${rp(repo)}/changes/${e.change_id}`}>{e.subject ?? e.kind}</Link>
            ) : (
              <Link to={`${rp(repo)}/commit/${e.new_sha}`}>{e.subject ?? "history import"}</Link>
            )}
          </ListItemTitle>
          {e.kind !== "land" && <Badge tone={e.kind === "revert" ? "red" : "default"}>{e.kind}</Badge>}
        </div>
        <ListItemMeta className="mt-1 gap-1.5">
          <Avatar name={who} size={16} />
          <b>{who}</b> {VERB[e.kind] ?? "updated main"} <TimeAgo ms={e.ts} />
          {e.task && (
            <>
              <ListItemSeparator /> <span className="font-mono">{e.task}</span>
            </>
          )}
          {e.evidence.pass || e.evidence.fail ? <ListItemSeparator /> : null}
          {e.evidence.pass ? (
            <span className="inline-flex items-center gap-1">
              <EvidenceIcon status="pass" /> {e.evidence.pass}
            </span>
          ) : null}
          {e.evidence.fail ? (
            <span className="inline-flex items-center gap-1">
              <EvidenceIcon status="fail" /> {e.evidence.fail}
            </span>
          ) : null}
        </ListItemMeta>
      </div>
      <span className="flex items-center gap-0.5">
        <Sha repo={repo} sha={e.new_sha} n={7} />
        <CopyButton value={e.new_sha} title="Copy full SHA" />
        <Tooltip content="Browse files at this commit">
          <Button asChild variant="ghost" size="icon-sm" className="max-md:hidden">
            <Link to={`${rp(repo)}/src/${e.new_sha}`} aria-label="Browse files at this commit">
              <FileCodeIcon size={14} />
            </Link>
          </Button>
        </Tooltip>
      </span>
    </ListItem>
  );
}

export function CommitView() {
  const { repo = "", sha = "" } = useParams();
  const q = useCommit(repo, sha);
  return (
    <Query q={q}>
      {(c) => {
        const a = parseSig(c.author);
        const body = messageBody(c.message);
        return (
          <>
            <Card className="mt-5">
              <CardContent>
                <div className="flex items-start gap-2">
                  <h2 className="min-w-0 flex-1 text-xl font-semibold">{c.message.split("\n", 1)[0]}</h2>
                  <Button asChild size="sm">
                    <Link to={`${rp(repo)}/src/${c.sha}`}>
                      <FileCodeIcon /> Browse files
                    </Link>
                  </Button>
                </div>
                {body && <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-muted-foreground">{body}</pre>}
              </CardContent>
              <CardContent className="flex flex-wrap items-center gap-2 bg-muted">
                <Avatar name={a.name} size={22} />
                <b>{a.name}</b>
                <span className="text-muted-foreground">&lt;{a.email}&gt;</span>
                <span className="text-muted-foreground">
                  authored <TimeAgo ms={a.time} />
                </span>
                {c.change_id && (
                  <Badge asChild tone="blue">
                    <Link to={`${rp(repo)}/changes/${c.change_id}`}>change {c.change_id.slice(-8)}</Link>
                  </Badge>
                )}
                <span className="ml-auto flex items-center gap-2.5 text-xs">
                  {c.parents.map((p) => (
                    <span key={p} className="flex items-center gap-1">
                      <span className="text-muted-foreground">parent</span> <Sha repo={repo} sha={p} n={7} />
                    </span>
                  ))}
                  <span className="flex items-center gap-1">
                    <span className="text-muted-foreground">commit</span> <span className="font-mono text-xs">{c.sha}</span>
                    <CopyButton value={c.sha} title="Copy SHA" />
                  </span>
                </span>
              </CardContent>
            </Card>
            <DiffView files={c.files ?? []} refs={{ repo, from: c.parents[0], to: c.sha }} />
          </>
        );
      }}
    </Query>
  );
}
